/**
 * **What does this combatant actually carry?** — the granted, core, pilot-frame and built-in
 * activate action catalogs a combatant brings into the fight, which tech-attack weapons are
 * legal for it, and the legality gate a granted `request_activate` passes through.
 */

import type { Affordance, AffordanceSource, Combatant, EncounterState } from "@lancer-vtt/domain";
import { affordanceKey, canUseProtocolAction, findAffordance, hasStatus, isCharacter, isItemDestroyed, sideOf } from "@lancer-vtt/domain";
import { ok, err } from "@lancer-vtt/shared";
import { resolveCoreAction, type CoreAction } from "../coreActionCatalog";
import { actionIdsOf, combatantEffectIds, deployedActionClausesOf, gatherActions, type LoadedOverlay } from "../effects/loader";
import { combatantRange } from "../effects/spatialAnswerer";
import { evaluatePredicate } from "../effects/predicate";
import { SEED_OVERLAY } from "../effects/seed";
import { QUICK_TECH_OPTIONS, legalQuickTechOptions } from "../quickTech";
import { checkActionAvailable } from "./actionAvailability";
import { isProtocolAction } from "./actionShapes";
import { isTurnVirgin } from "./ordnance";
import type { BudgetVerdict } from "./types";
import { affordable } from "../events/gameEvents";

/**
 * The **tech-attack legality wall**: the set of weapon/carrier ids legal to fire as a TECH attack
 * (`intent.tech === true`) for `attacker`. Without this wall, `intent.weapon` goes unvalidated on
 * the tech branch entirely — {@link checkAttackWeapon} above is skipped whenever `intent.tech` —
 * so any pack entry id could be named and its attack-family clauses inherited (Power Knuckles'
 * Prone, Concussion Missiles' Impaired, Brutal's Knockback, Skirmisher's `remove_status`, all
 * reachable with zero dice at Sensors range).
 *
 * **The builtin set derives from {@link QUICK_TECH_OPTIONS}'s own `route.kind === "tech_attack"`
 * entries — never a restated literal** — so the wall can't drift from the menu it mirrors (that
 * set is exactly `{"act_invade"}`).
 *
 * **Widened per-attacker**: the admitted set is **builtins ∪
 * `Object.keys(attacker.base.techAttackActions ?? {})`** — the frozen fact `spawn.ts`'s
 * `freezeActionIds` stamps at spawn for every equipped system whose RAW action declares
 * `attack === "tech"` (Markerlight and the licensed family behind it). Still **compendium-free**
 * (the frozen map is the answer, never a live lookup) and still a pure derivation off
 * `state`/`attackerId` with no other input, so both call sites in `refereeIntent.ts` (the tech
 * direction AND the symmetric non-tech-must-not-name-a-tech-id direction) keep their exact call
 * shape. An attacker with no frozen `techAttackActions` (an NPC, a pilot, a mech with no
 * tech-attack-granting gear) still only admits the builtin set — no accidental widening for a
 * combatant that never froze the map.
 */
export function legalTechAttackWeaponIds(state: EncounterState, attackerId: string): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const option of QUICK_TECH_OPTIONS) {
    if (option.route.kind === "tech_attack") ids.add(option.route.weapon);
  }
  const frozen = state.combatants[attackerId]?.base.techAttackActions;
  if (frozen) for (const id of Object.keys(frozen)) ids.add(id);
  return ids;
}

/**
 * Parse a composite **granted-action id** `<itemId>:<actionIndex>` into its parts; `null` if `id`
 * isn't that shape (no colon, or a non-integer/negative suffix).
 *
 * **This is a WIRE/DISPLAY parser, not an economy reader** (`decisions/0176`). Since
 * `affordance-table` Phase 3.1 no engine reader derives a ledger key or a cap from a parsed string:
 * a wire id becomes structure exactly once, at {@link resolveAffordance}, and every downstream read
 * takes the resolved {@link Affordance}'s `source`/`key`. What is left of this function is three
 * uses, all of them safe because the class is already known at the call site:
 *   1. {@link isActivateActionCarried}'s composite→bare normalization, scoped to the granted class
 *      only (see its doc for the talent-rank false-accept that scoping exists to avoid);
 *   2. `actionRegistry.ts`'s compendium lookups, which turn a granted composite back into the item
 *      + clause it displays;
 *   3. `resolveAffordance`'s own bare/composite matching inside the door.
 *
 * **Do NOT call it with a talent id.** `t_leader:1` parses happily to
 * `{itemId: "t_leader", index: 1}` and the `1` means RANK, not an action-array index. The rule is
 * unchanged by the refactor and is the reason the door resolves the CLASS before it looks at a
 * colon: class first, colon second.
 *
 * Lives here rather than in `actionRegistry.ts`, which imports this copy (no cycle:
 * `actionRegistry.ts` already imports from `economy.ts`-adjacent modules, and `economy.ts` imports
 * nothing from `actionRegistry.ts`). `effects/loader.ts`'s `bareGrantedId` stays a deliberately
 * separate, loader-local copy (its own doc explains why: it's upstream of this module and importing
 * back would cycle) — this export is for `actionRegistry.ts` and any other **downstream** consumer,
 * not a replacement for that one.
 */
export function parseGrantedId(id: string): { itemId: string; index: number } | null {
  const colon = id.lastIndexOf(":");
  if (colon <= 0 || colon === id.length - 1) return null;
  const index = Number(id.slice(colon + 1));
  if (!Number.isInteger(index) || index < 0) return null;
  return { itemId: id.slice(0, colon), index };
}

/**
 * The set of granted-action ids `combatant` actually **carries** right now — the granted keys of
 * `base.affordances` (composite, spawn-frozen) **UNION** the same enumerate-time overlay mint
 * `enumerateActions` computes (`actionRegistry.ts`), extracted so the two never restate each other.
 * A wall built from the frozen list alone would wrongly reject a real
 * Custom-Paint-Job/Type-I-Flight-System carrier, because their composite ids exist ONLY as this
 * ephemeral mint, never cached onto frozen state.
 *
 * The mint is re-derived fresh on every call, scoped to `combatant.base.equippedSystems` — it can
 * only ever produce an id for a system this combatant genuinely equips, never the corpus at large
 * — mirroring `enumerateActions`' own comment on the same walk.
 *
 * **A destroyed system's actions are excluded HERE**, inside the shared derivation, not left as an
 * enumerate-side display filter: "does this combatant currently carry this action" is exactly what
 * System Trauma's destroy answers `false` to (Core p.80), and the wall must honor that the same way
 * the bar does — a destroyed system's action legal to *activate* while invisible on the *bar* would
 * be an inconsistency with no RAW basis.
 *
 * **The canonical-duplicate suppression (`CANONICAL_DUPLICATE_GRANTED_ITEM_IDS`,
 * `actionRegistry.ts`) is deliberately NOT applied here.** That suppression hides a
 * permanently-inert duplicate *row* from the bar — see its own doc comment — because an
 * already-live core row renders the identical affordance; it is not a statement about possession.
 * The combatant genuinely equips the system and its composite id is genuinely frozen/minted, so a
 * wall that called it illegal would be false: the actor DOES carry it. Left to `enumerateActions`,
 * the display-only consumer that owns the dedup decision.
 *
 * Order is preserved (the frozen records first, then the overlay mint, both minus destroyed) for
 * the caller that cares about display order (`enumerateActions`); {@link isActivateActionCarried}'s
 * membership test doesn't care about order.
 *
 * **This is now a thin projection of {@link carriedAffordances}** (`affordance-table` Phase 3.1):
 * the record-shaped twin answers the live question over `base.affordances`, and this function maps
 * its result to `key`s. Both therefore walk the SAME {@link overlayMintedGrantedAffordances} and
 * {@link destroyedSystemItemIds} by construction rather than by convention — there is one derivation
 * left, not two that have to agree. Prefer `carriedAffordances` in new code; this id-shaped view
 * survives for callers that genuinely want a membership set of wire ids.
 */
export function carriedGrantedActionIds(combatant: Combatant, overlay: LoadedOverlay = SEED_OVERLAY): Set<string> {
  return new Set(carriedAffordances(combatant, overlay).map((a) => a.key));
}

/**
 * The **enumerate-time overlay mint**, extracted from {@link carriedGrantedActionIds} so its
 * id-shaped and record-shaped consumers walk the SAME loop instead of two that can drift.
 *
 * A bare-id gather returns EVERY `kind:"action"` clause the overlay authors for an entry — its
 * length IS that system's authored affordance count, re-derived fresh on every call, and position
 * `i` is the `:N` the mint addresses. Scoped to `base.equippedSystems` and `base.pilotGearEffectIds`
 * (an on-foot pilot's gear: Mobility Hardsuit's Fly, `move-family-opt-in-surface` Phase 2), so it can
 * only ever produce an affordance for an item this combatant genuinely carries, never the corpus at
 * large. A mech holds its pilot's gear in the frozen pilot block, not in `base`, so a gear row never
 * reaches a mech's bar.
 *
 * `alreadyMinted` is the dedupe set the caller already holds (the frozen ids, or the frozen
 * records' keys): a system whose compendium `actions[]`/`deployables[]` entry is ALSO frozen at
 * this same `:N` position would otherwise double the row. The in-loop `some(...)` check is the
 * second dedupe, against a duplicate *equip* of the same system.
 *
 * **The cap comes from the authored clause, not from a frozen map.** These ids exist only as this
 * ephemeral mint — `ms_custom_paint_job:0` and `ms_type_i_flight_system:0` carry no `actions[]`,
 * no `deployables[]` and no `tg_limited` in `lancer-data` — so `freezeActionCaps` can never
 * reach them; `freezeOverlayAffordances` (`spawn/actionFreeze.ts`) freezes their RECORD at spawn
 * by reading this same `limitedUses` facet off this same clause list. Reading the facet here
 * reproduces that pool exactly (`limited: {max}` — the fact is fixed, not authored). **Recharge is always absent**: no mech/pilot spawn path mints a `recharge` target at
 * all, and this mint walks only systems and pilot gear, so an overlay-minted affordance has no
 * Recharge target to carry.
 */
function overlayMintedGrantedAffordances(args: {
  combatant: Combatant;
  overlay: LoadedOverlay;
  alreadyMinted: ReadonlySet<string>;
}): Affordance[] {
  const { combatant, overlay, alreadyMinted } = args;
  const minted: Affordance[] = [];
  const itemIds = [...(combatant.base.equippedSystems ?? []).map((s) => s.id), ...(combatant.base.pilotGearEffectIds ?? [])];
  for (const itemId of itemIds) {
    const clauses = gatherActions({ ownerId: combatant.id, entityId: itemId, overlay });
    clauses.forEach((clause, i) => {
      const source = { kind: "granted", itemId, index: i } as const;
      const key = affordanceKey(source);
      if (alreadyMinted.has(key) || minted.some((a) => a.key === key)) return;
      const max = clause.effect.limitedUses?.max;
      minted.push({ key, source, ...(max === undefined ? {} : { limited: { max } }) });
    });
  }
  return minted;
}

/**
 * The item ids of every equipped system this combatant has had **destroyed** — the shared
 * exclusion both {@link carriedGrantedActionIds} and {@link carriedAffordances} apply. System
 * Trauma's destroy answers "no" to "does this combatant currently carry this action" (Core p.80),
 * and the wall must honor that the same way the bar does.
 */
export function destroyedSystemItemIds(combatant: Combatant): Set<string> {
  return new Set(
    (combatant.base.equippedSystems ?? [])
      .filter((s) => isItemDestroyed(combatant.current.resources, s.instanceId))
      .map((s) => s.id),
  );
}

/**
 * The **affordance records** `combatant` actually carries right now — the record-shaped twin of
 * {@link carriedGrantedActionIds}, and the answer to "what can this combatant activate" once the
 * readers take records instead of string keys (`decisions/0176`).
 *
 * **`base.affordances` alone is NOT the answer, and that is the whole reason this exists.** Some
 * composite ids exist ONLY as the enumerate-time overlay mint, never cached onto frozen state —
 * Custom Paint Job and Type-I Flight System are the named carriers — so a derivation reading only
 * the frozen table would wrongly report a real carrier as carrying nothing. This mints records for
 * that half at call time exactly as `carriedGrantedActionIds` mints their ids, sharing the same
 * walk ({@link overlayMintedGrantedAffordances}) rather than restating it.
 *
 * **`overlay` is REQUIRED, deliberately — never give this a `SEED_OVERLAY` default.** The mint
 * runs through the overlay, so a defaulted overlay makes the derivation blind to every GM-loaded
 * entry: it would silently report a world-pack carrier as carrying nothing at all.
 * `carriedGrantedActionIds` carries that default today for its own historical callers; it is not
 * a pattern to copy into new code.
 *
 * **Order matches `carriedGrantedActionIds` exactly** — frozen first (in `base.affordances`'
 * order, which is the order the freezers walk the loadout in), then the mint, both minus a
 * destroyed system's — so `carriedAffordances(c, o).map(a => a.key)` is the same sequence
 * `carriedGrantedActionIds(c, o)` yields. Since Phase 3.1 that is true by construction:
 * `carriedGrantedActionIds` is literally that `map`. `affordanceTable.pin.test.ts` pins it.
 *
 * **Granted class only**, exactly like the function it mirrors. Frame traits, core systems, core
 * bonuses, talent ranks and NPC-feature effect ids are the *effect* class: they reach the wall
 * through {@link carriedPilotFrameActionIds} by exact match, they have no composite equivalent,
 * and their frozen records (where they have one) are filtered out here rather than folded in — the
 * two classes stay two derivations, as `isActivateActionLegal` already has them.
 *
 * **The canonical-duplicate suppression is deliberately NOT applied**, matching
 * {@link carriedGrantedActionIds}: that suppression hides a permanently-inert duplicate *row* from
 * the bar and is not a statement about possession.
 */
export function carriedAffordances(combatant: Combatant, overlay: LoadedOverlay): Affordance[] {
  const frozen = (combatant.base.affordances ?? []).filter((a) => a.source.kind === "granted");
  const minted = overlayMintedGrantedAffordances({
    combatant,
    overlay,
    alreadyMinted: new Set(frozen.map((a) => a.key)),
  });
  const destroyed = destroyedSystemItemIds(combatant);
  return [...frozen, ...minted].filter((a) => !(a.source.kind === "granted" && destroyed.has(a.source.itemId)));
}

/**
 * Resolve `combatant.base.coreActionIds` (the per-kind composite core-action set every combatant
 * unconditionally gets at spawn — `coreActionIdsFor`, `coreActionCatalog.ts`) against the global
 * catalog, in frozen order, with the SAME unique-bare-id invariant `enumerateActions`' core loop
 * enforces — extracted, not restated, so the bar and the wall consume ONE resolution instead of
 * two. An unknown/off-shape composite honest-degrades (skipped), matching the granted seam's
 * missing-id handling; a genuine bare-id
 * collision between two composites (structurally possible for a future composite-kind combatant,
 * not reachable by any kind today) still throws — that invariant protects every downstream
 * consumer that keys on the bare id (the budget ledger, reaction tracking, `apply_resolved`
 * batches, the frontend aim-mode), not just the bar.
 */
export function resolvedCoreActions(combatant: Combatant): { composite: string; action: CoreAction }[] {
  const resolved: { composite: string; action: CoreAction }[] = [];
  const claimedBy = new Map<string, string>(); // bare id -> the composite that already claimed it
  for (const composite of combatant.base.coreActionIds ?? []) {
    const action = resolveCoreAction(composite);
    if (action === undefined) continue; // unknown composite — honest degrade
    const prior = claimedBy.get(action.id);
    if (prior !== undefined) {
      throw new Error(
        `coreActionIds invariant (decisions/0076): combatant "${combatant.id}" resolves both "${prior}" and ` +
          `"${composite}" to the same bare id "${action.id}" — the outward descriptor id would conflate them ` +
          `(the budget ledger / reaction tracking / aim-mode all key on the bare id). A combatant's core set ` +
          `must resolve to unique bare ids.`,
      );
    }
    claimedBy.set(action.id, composite);
    resolved.push({ composite, action });
  }
  return resolved;
}

/**
 * The **bare** core-action ids `combatant` legitimately carries — `enumerateActions`' `source:
 * "core"` rows, id-only ({@link resolvedCoreActions} does the resolution). Per-kind by
 * construction (`base.coreActionIds` is frozen per-kind at spawn — mech/drone/object get the
 * `MECH_ACTIONS` table, an NPC `NPC_ACTIONS`, a pilot-on-foot `PILOT_ACTIONS`), so this already
 * satisfies "the wall's set must match per-combatant" for the core axis without any extra branch
 * here.
 */
export function carriedCoreActionIds(combatant: Combatant): Set<string> {
  return new Set(resolvedCoreActions(combatant).map((r) => r.action.id));
}

/**
 * The **pilot/frame/npc-feature-effect** granted-action ids `combatant` legitimately carries —
 * talent ranks (`t_leader:1`), core bonuses (`cb_overpower_caliber`), frame traits/core systems
 * (`everest_initiative`, `everest_hyperspec_fuel_injector`), and NPC Trait/Reaction/Tech feature
 * ids (`npcFeatureEffectIds`, Gap A) that actually gather at least one `action`-kind overlay
 * clause (most talent ranks/core bonuses are passive and gather none — this mirrors
 * `enumerateActions`' own `pilotEffects` filter exactly, extracted so the two can't drift).
 * Source is `combatantEffectIds(combatant, { pilotGear: false })` — **deliberately excludes `base.pilotGearEffectIds`**, the same
 * exclusion `enumerateActions` documents (`actionRegistry.ts`): pilot gear's activatable actions
 * already reach the wall through {@link carriedGrantedActionIds}'s composite granted-record branch,
 * and pooling `pilotGearEffectIds` here too would mint a second, uncapped row for the same
 * possession. Every fielded Phase-1 NPC Trait/Reaction feature that carries `actions[]` already
 * reaches the wall the same composite way (frozen into `base.affordances` by `spawnNpc`); this
 * branch stays in place for the (today theoretical) case of a Trait/Reaction/Tech feature
 * authoring a bare-id `kind:"action"` clause directly on its own feature id.
 *
 * An entry whose action clauses are named ({@link import("../effects/atoms").ActionEffect.name})
 * contributes `<id>#<name>` per clause instead of its own id, so each is activated alone.
 *
 * **Every id this can produce is either bare (core bonus, frame trait/core system, NPC feature)
 * or carries a RANK suffix (`<talentId>:<rank>`), NEVER an `<itemId>:<actionIndex>` composite**
 * — see {@link isActivateActionCarried}'s doc for why that distinction is load-bearing for
 * membership testing (the rank suffix collides lexically with the granted seam's composite
 * shape, but means something structurally different, and must not be normalized the same way).
 */
export function carriedPilotFrameActionIds(combatant: Combatant, overlay: LoadedOverlay = SEED_OVERLAY): Set<string> {
  const ids = new Set<string>();
  for (const id of combatantEffectIds(combatant, { pilotGear: false })) {
    if (gatherActions({ ownerId: combatant.id, entityId: id, overlay }).length === 0) continue;
    const named = actionIdsOf(id, overlay);
    for (const actionId of named.length > 0 ? named : [id]) ids.add(actionId);
  }
  return ids;
}

/**
 * The action ids the statuses on `combatant` author: a status the afflicted combatant clears by a
 * check (`Hack/Slash#Reboot core computer`), or Sniper's Mark's free `Sniper's Mark#Drop Prone`.
 * Addressed like a pilot effect's ({@link actionIdsOf}): `<status>#<name>` per named clause, else
 * the status name. Only the status's carrier takes it, so another combatant's status never
 * reaches this set.
 */
export function carriedStatusActionIds(combatant: Combatant, overlay: LoadedOverlay = SEED_OVERLAY): Set<string> {
  const ids = new Set<string>();
  for (const { name } of combatant.statuses) {
    for (const actionId of actionIdsOf(name, overlay)) ids.add(actionId);
  }
  return ids;
}

/** One action a deployed drone offers `actorId` (`ActionEffect.usableBy`): its `<entryId>#<name>` id and the drone that carries it. */
export type DeployedActionSource = { actionId: string; droneId: string };

/**
 * The actions **deployed drones carry for `actorId`** (`ll1-drones` D6): for every placed, not
 * Destroyed combatant some entry deployed (`deployedFrom`), on the actor's side, each of that
 * entry's `usableBy` clauses whose `within` reaches the actor (footprint to footprint). The owner
 * is not special: RAW Restock's "allied characters" include the deployer when adjacent. Only a
 * character activates one. Drone-id order; one row per drone, so two drones offer the action twice.
 */
export function deployedActionSources(state: EncounterState, actorId: string, overlay: LoadedOverlay): DeployedActionSource[] {
  const actor = state.combatants[actorId];
  if (actor === undefined || !isCharacter(actor) || actor.pos == null) return [];
  const out: DeployedActionSource[] = [];
  for (const droneId of Object.keys(state.combatants).sort()) {
    const drone = state.combatants[droneId]!;
    const from = drone.deployedFrom;
    if (from === undefined || drone.pos == null || hasStatus(drone.statuses, "Destroyed") || droneId === actorId) continue;
    if (sideOf(drone) !== sideOf(actor)) continue;
    for (const { id, clause } of deployedActionClausesOf(from.entryId, overlay)) {
      if (clause.usableBy!.side === "owner" && from.combatantId !== actorId) continue;
      const range = combatantRange(state, actorId, droneId);
      if (range !== null && range <= clause.usableBy!.within) out.push({ actionId: id, droneId });
    }
  }
  return out;
}

/**
 * The deployed-action gate (`ll1-drones` D6), for a `request_activate` naming a `usableBy` clause:
 * `target` must be a drone that offers it to `actorId` now ({@link deployedActionSources}: side and
 * reach), and the clause's `condition` must hold with `self` = the actor and `target` = the drone
 * (Restock's Priming). `undefined` when `action` is no deployed action or the gate passes; else the
 * refusal. Checked before any spend, so a refused activation costs nothing.
 */
export function deployedActionRefusal(args: { state: EncounterState; actorId: string; action: string; target: string | undefined; overlay: LoadedOverlay }): string | undefined {
  const { state, actorId, action, target, overlay } = args;
  const hash = action.lastIndexOf("#");
  const clause = hash > 0 ? deployedActionClausesOf(action.slice(0, hash), overlay).find((c) => c.id === action)?.clause : undefined;
  if (clause === undefined) return undefined;
  const carriers = deployedActionSources(state, actorId, overlay).filter((s) => s.actionId === action).map((s) => s.droneId);
  if (target === undefined || !carriers.includes(target)) {
    return `activate: "${action}" is carried by an allied drone within ${clause.usableBy!.within} — name it as the target (${carriers.length > 0 ? carriers.join(", ") : "none in reach"})`;
  }
  if (clause.condition !== undefined && !evaluatePredicate(clause.condition, { state, actors: { self: actorId, attacker: actorId, target } })) {
    return `activate: "${action}" is not available on ${state.combatants[target]?.name ?? target} yet`;
  }
  return undefined;
}

/**
 * The **universal Quick Tech builtins** (`act_scan`, `act_lock_on`, `act_bolster` —
 * `effects/seedEntries.ts`) `combatant` legitimately carries. These are NOT compendium ids and
 * never freeze onto any `base.*Ids` list at all: `act_bolster` fires bare from real
 * CLI proofs (`cli/proofs/bolster.test.ts`, `cli/proofs/jammed-gate.test.ts`) with zero
 * freeze/mint vector, because RAW grants them to every PC-side combatant just for existing (Core
 * p.67/280). Derived from {@link legalQuickTechOptions} — the SAME menu-legality seam the
 * frontend's `QuickTechChooser`/`FullTechChooser` already call (`CombatWindows.tsx`) — filtered
 * to its `route.kind === "activate"` entries, rather than restating `QUICK_TECH_ACTIVATE_IDS`'s
 * literal (a budget-ledger convenience, not a legality source) a second time.
 *
 * **Per-kind, not a global constant**: `legalQuickTechOptions`
 * narrows an NPC actor (`combatant.kind === "npc"`) to Invade-or-Lock-On only (RAW Core p.280 — an
 * NPC base statblock doesn't carry Bolster/Scan), which among the `activate`-kind options leaves
 * only `act_lock_on` — so an NPC's builtin set is `{act_lock_on}`, every other kind's (mech,
 * pilot-on-foot, drone, object) is `{act_scan, act_lock_on, act_bolster}`. No target-immunity
 * filtering here (`targetTechBlocked` is left unset) — that's a per-target legality axis this
 * carrier-ownership wall doesn't own; it's already enforced independently in
 * `refereeIntent.ts`'s pre-dice ladder.
 */
export function carriedBuiltinActivateIds(combatant: Combatant): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const option of legalQuickTechOptions({ npcActor: combatant.kind === "npc" })) {
    if (option.route.kind === "activate") ids.add(option.route.action);
  }
  return ids;
}

/**
 * The **granted-action carrier-ownership wall** — the `request_activate` twin of
 * {@link legalTechAttackWeaponIds}: the FULL set of activatable ids `actorId` legitimately
 * carries — `enumerateActions`' three sources (core, granted, pilot/frame-effect) plus the
 * universal Quick Tech builtins, single-sourced with the bar per source so wall and menu cannot
 * drift. Shaped on `legalTechAttackWeaponIds`'s `(state, attackerId)` call shape.
 *
 * **TODO: this function is exported but not yet rung at any call site.** Ring it at
 * `backend/src/encounterRoom.ts`'s pre-dice ladder and `refereeCommand.ts`'s
 * `preparePreApplyGate` (mirroring `checkGrantedActionLegality`'s own two sites) once the
 * room-vs-referee placement question is settled — the derivation itself is identical either way.
 *
 * **This flat `ReadonlySet<string>` is for display/cross-check use** (comparing against
 * `enumerateActions`' full row-id set) — **do NOT** feed it straight into
 * {@link isActivateActionCarried} as a membership test; that helper's composite→bare
 * normalization is scoped to the granted class only and would mis-accept a bare talent query
 * against a ranked id in this merged set (see that function's doc). {@link
 * isActivateActionLegal} is the correctly-scoped full-universe membership predicate — use that at
 * a ring site, not `legalActivateActionIds(...).has(id)`/`isActivateActionCarried`.
 */
export function legalActivateActionIds(state: EncounterState, actorId: string, overlay: LoadedOverlay = SEED_OVERLAY): ReadonlySet<string> {
  const combatant = state.combatants[actorId];
  if (!combatant) return new Set();
  return new Set<string>([
    ...carriedGrantedActionIds(combatant, overlay),
    ...carriedPilotFrameActionIds(combatant, overlay),
    ...carriedStatusActionIds(combatant, overlay),
    ...deployedActionSources(state, actorId, overlay).map((s) => s.actionId),
    ...carriedCoreActionIds(combatant),
    ...carriedBuiltinActivateIds(combatant),
  ]);
}

/**
 * Membership test for {@link carriedGrantedActionIds}'s set ONLY (mech systems, pilot gear, NPC
 * features), normalizing **composite → bare** the SAME direction `gatherActions`' own fallback
 * does (`effects/loader.ts`): a query naming the bare id (`pg_patch`) is accepted when the
 * carrier's set holds the composite (`pg_patch:0`).
 *
 * Real, not hypothetical: **`ms_active_camouflage`** fires BARE in `cli/proofs/active-camouflage.test.ts`
 * and COMPOSITE (`ms_active_camouflage:0`) in `cli/proofs/weapon-ordnance.test.ts` (round 4) — the same entry,
 * both shapes, so a membership test accepting only one breaks whichever proof uses the other.
 * (This doc long paired that specimen with `ms_pattern_b_hex_charges`; re-measured in
 * `affordance-table` Phase 3.2, all four of that system's activate call sites (two each in `cli/proofs/hex-mine.test.ts` and
 * `cli/proofs/hex-charges.test.ts`) are composite, and none is bare.
 * It is a two-clause specimen, not a two-shape one. Removed rather than reworded.)
 *
 * **The reverse direction — a COMPOSITE query against a set holding only a BARE id — was checked,
 * not assumed, and does not arise for these classes.** Every id {@link carriedGrantedActionIds}
 * can produce is already composite: `freezeActionIds` (`spawn/actionFreeze.ts`) and
 * `freezePilotGearActionIds` (`spawn/pilot.ts`) mint `<itemId>:<index>` exclusively, and the
 * overlay-mint union mints the identical `${sys.id}:${i}` shape. There is no path that freezes a
 * bare id into the carried set for mech systems, pilot gear, or NPC features — so this function
 * needs no bare→composite direction.
 *
 * **Do NOT call this with `legalActivateActionIds`'s merged output, or with
 * {@link carriedPilotFrameActionIds}'s set** — a talent rank id (`t_leader:1`) is lexically
 * indistinguishable from a granted composite (`<itemId>:<actionIndex>`):
 * `parseGrantedId("t_leader:1")` happily parses to `{itemId: "t_leader", index: 1}`, even though
 * the `1` means RANK, not an action-array index. Fed through this helper's fallback loop, a bare
 * query `"t_leader"` would then wrongly match a carrier holding only `t_leader:1`, treating "any
 * rank of this talent" as equivalent to a specific rank. That false-accept never legitimately
 * arises today (`cli/proofs/inspiring-presence.test.ts` always fires the FULL rank-suffixed id,
 * never bare), but a wall must not carry a known false-accept just because nothing exercises it.
 * Every other class (core bonuses, frame traits/core systems, core-action ids, the Quick Tech
 * builtins) is bare with no colon at all, so exact membership is not just safe but the ONLY
 * correct test for them — normalizing would be inventing leniency the real emit shapes never ask
 * for. Use {@link isActivateActionLegal} for the full-universe check; it applies this
 * normalization to exactly the one class that demands it and exact-matches everywhere else.
 */
export function isActivateActionCarried(carried: ReadonlySet<string>, queriedId: string): boolean {
  if (carried.has(queriedId)) return true;
  for (const id of carried) {
    if (parseGrantedId(id)?.itemId === queriedId) return true;
  }
  return false;
}

/**
 * The verdict {@link resolveAffordance} returns. `kind: "affordance"` carries the ONE frozen
 * record the wire id names — its `key` is the ledger slot, its `cap`/`recharge` the frozen
 * economy facts, so a caller holding it never indexes a string-keyed map again.
 * `kind: "ambiguous"` carries every granted composite a bare query matched, in ascending
 * affordance-index order, so the caller can name them in a rejection rather than silently
 * picking one.
 */
export type AffordanceResolution =
  | { readonly kind: "affordance"; readonly affordance: Affordance }
  | { readonly kind: "ambiguous"; readonly matches: readonly string[] };

export type ResolveAffordanceArgs = {
  state: EncounterState;
  actorId: string;
  /** The overlay entity id a `request_activate` names — bare (`pg_patch`) or composite (`pg_patch:0`). */
  wireId: string;
  /**
   * **Required, deliberately — never give this a `SEED_OVERLAY` default.**
   * {@link carriedAffordances} mints through the overlay, so a defaulted overlay makes this door
   * blind to every GM-loaded entry: it would resolve a world-pack carrier's own action to a
   * capless, rechargeless stranger. `carriedGrantedActionIds` carries that default today for its
   * historical callers; it is not a pattern to copy into new code.
   */
  overlay: LoadedOverlay;
};

/**
 * **The one parser at the door** (`decisions/0176` ruling 3) — resolve the wire id a
 * `request_activate` (or a `prepare`'s `readiedActionId`) names into the carrier's ONE frozen
 * {@link Affordance} record, so every reader downstream takes the record instead of re-deriving
 * a string key against a class it has to already know.
 *
 * The wire stays the composite string — the browser and every `cli/proofs/` test emit it, and
 * intents are not persisted for replay — so exactly one place turns that string into structure,
 * and this is it.
 *
 * **The class is resolved by which carried set holds the id, BEFORE any colon parse.** That
 * ordering is the whole safety property: `t_leader:1` is a talent *rank*, lexically identical to
 * a granted composite, and it is never in the granted set, so it can never be read as an action
 * index. Widening the set matched against re-opens that false-accept.
 *
 *   1. **Exact composite** — the wire id IS one of this carrier's granted affordance keys. The
 *      common case; every browser-emitted id.
 *   2. **Bare granted** — the wire id names an *entry*, matched on `source.itemId` against the
 *      carrier's **actual** records. One match resolves; **several is `ambiguous`, never a pick**
 *      (the granted class mints one record per affordance precisely so each is separately
 *      addressable, capped and discharged). **Never construct `` `${wireId}:0` ``**: the index is
 *      a position in `decisions/0123`'s unified affordance space (`actions[]` then
 *      `deployables[]`), so `:0` is only incidentally right for today's bare-fired entries and
 *      silently names the wrong affordance for the first deployable-bearing one
 *      (`ms_pattern_a_smoke_charges` mints `:0` for its grenade AND `:1` for its mine).
 *   3. **Everything else — exact match, unchanged.** Frame traits, frame core systems, core
 *      bonuses, talent ranks, core actions and the Quick Tech builtins are the *effect* class:
 *      the bare id is the whole and correct address, it has no composite equivalent, and its cap
 *      is frozen **entry-level** by construction. One bare `everest_hyperspec_fuel_injector`
 *      fires both of Power Up's authored clauses and consumes the one 1/scene cap frozen for the
 *      entry — an entry-level fire is the only thing that could consume it correctly. So "one
 *      bare id, several clauses" is a **rejection** in the granted class and **required
 *      behaviour** here, and this arm must never be folded into arm 2.
 *
 * **Not every activatable id has a frozen record**, and the door must not assume one.
 * `base.affordances` holds the granted class plus only those effect-class entries that carry a
 * frozen cap (`freezeFrameTraitCaps`' key set); passive traits, core bonuses and most talent ranks
 * freeze nothing. Arm 3 therefore looks the key up in the frozen table and, finding none,
 * synthesizes a capless `effect` record.
 *
 * **There is no legacy fallback and there must not be one.** Through Phases 1-2 this door derived
 * records from `base.actionIds` + the old maps for a base that carried no record table, so the ~26
 * hand-built test fixtures of the day kept working while the expand ran. `affordance-table` Phase
 * 3.1 deleted the maps and the fallback with them; a base with no `affordances` now simply carries
 * no granted affordance, and the encounter schema-version bump is what makes a world persisted
 * before the table existed fail loudly rather than load with a silently empty economy
 * (`decisions/0176`, "No migration path" — one live world, hand-fixed).
 *
 * An unknown actor degrades to a bare synthesized record (mirroring {@link isActivateActionLegal}'s
 * degrade-don't-throw posture); the carrier-ownership wall is what rejects an unknown actor.
 */
export function resolveAffordance(args: ResolveAffordanceArgs): AffordanceResolution {
  const { state, actorId, wireId, overlay } = args;
  const combatant = state.combatants[actorId];
  if (!combatant) return { kind: "affordance", affordance: { key: wireId, source: { kind: "effect", entityId: wireId } } };
  const granted = carriedAffordances(combatant, overlay);
  // 1. Exact composite — the id IS one of this carrier's granted affordance keys.
  const exact = granted.find((a) => a.key === wireId);
  if (exact) return { kind: "affordance", affordance: exact };
  // 2. Bare granted — match the ENTRY against the carrier's actual records, never `${wireId}:0`.
  const matches = granted.filter((a) => a.source.kind === "granted" && a.source.itemId === wireId);
  if (matches.length === 1) return { kind: "affordance", affordance: matches[0]! };
  if (matches.length > 1) {
    const sorted = [...matches].sort(
      (a, b) => (a.source.kind === "granted" ? a.source.index : 0) - (b.source.kind === "granted" ? b.source.index : 0),
    );
    return { kind: "ambiguous", matches: sorted.map((a) => a.key) };
  }
  // 3. Every other class — exact match, unchanged.
  return { kind: "affordance", affordance: exactRecordFor(combatant, wireId) };
}

/**
 * {@link resolveAffordance}'s arm 3: the frozen record for `key` by **exact** lookup — the
 * effect class (frame traits, frame core systems, core bonuses, talent ranks), the core actions,
 * the Quick Tech builtins, and any id this carrier does not hold at all.
 *
 * A hit in `base.affordances` is the record; a miss synthesizes a capless, rechargeless `effect`
 * record — exactly what the deleted `base.actionUsageCaps?.[id]` / `base.actionRecharge?.[id]` raw
 * reads produced when the maps had no entry for the id, which is the common case for the effect
 * class (most passive traits, core bonuses and talent ranks freeze no record at all).
 *
 * **The synthesized source is ALWAYS `effect`, and never a parse of the key.** (Through Phases 1-2
 * the deleted `legacyRecordFor` derived this source with `parseGrantedId`, which is exactly the
 * false-accept described next; Phase 3.1 deleted it with the rest of the legacy fallback.) This is
 * arm 3, which by construction is *every class but granted* — the granted arms already ran and
 * missed — so the class is settled before we get here and there is nothing left to infer from the
 * string. Parsing anyway is precisely the false-accept `resolveAffordance`'s "class first, colon
 * second" ordering exists to prevent: `t_leader:1` is a talent RANK, and reading its `:1` as an
 * action index makes its `source` name a nonexistent `t_leader` entry at clause 1 — invisible while
 * only `key` was consumed, and a broken clause gather the moment `source` drives one
 * ({@link import("../effects/loader").gatherActions}' structured path, `affordance-table` 1.5).
 *
 * **The lookup is deliberately not filtered by carriage.** The old raw read wasn't either, so a
 * cap frozen for an id the carrier no longer gathers (a destroyed system's composite, a frame
 * entry whose overlay clause went away) still gates exactly as it did. The carrier-ownership wall
 * ({@link isActivateActionLegal}) is what rejects a non-carried id, and it stays a separate check.
 */
function exactRecordFor(combatant: Combatant, key: string): Affordance {
  return findAffordance(combatant.base.affordances, key) ?? { key, source: { kind: "effect", entityId: key } };
}

/**
 * The correctly-scoped full-universe membership predicate for `request_activate`'s carrier-
 * ownership wall — the function a future ring site should call, NOT
 * `legalActivateActionIds(...).has(id)` (see that function's own doc for why the flat set is
 * display-only). Composes the four sources exactly as {@link legalActivateActionIds} does, but
 * applies {@link isActivateActionCarried}'s composite→bare normalization ONLY to the granted
 * class — the one class the real `cli/proofs/` corpus demands it for — and tests
 * every other class (pilot/frame effects, core actions, Quick Tech builtins) by exact match, per
 * that helper's doc. An unknown actor degrades to `false` (mirrors `legalActivateActionIds`'s
 * empty-set degrade), never a throw.
 *
 * **DO NOT DELETE THE GRANTED LEG. It is the ONLY check on one live path** — settled
 * `decisions/0143` + `decisions/0176`, and enforced by a test, not just by this comment.
 *
 * The path it uniquely catches: **a cap frozen BARE for an id the carrier does not hold.** Such an
 * id reaches {@link resolveAffordance}'s arm 3 by exact lookup, so it resolves successfully and
 * looks granted-shaped, the prepare/activate ladder is entered — and nothing downstream asks
 * whether the actor actually carries it. This leg is what rejects it, and it rejects it in the
 * vocabulary of possession (`does not carry "<id>"`) rather than the catch-all's vocabulary
 * complaint.
 *
 * The tempting-but-wrong reasoning to head off: the door resolves a granted id by matching
 * `source.itemId` against the carrier's OWN records, so *for any id the door resolves that way*
 * resolution success already implies possession — which reads as "this leg is redundant now."
 * It is not. That implication covers the door's granted arms only; the bare-frozen-cap id above
 * comes back through arm 3, where possession was never consulted. `decisions/0143` made
 * role-blind carrier ownership a second, independent wall and `decisions/0176` moved the keying
 * without collapsing it.
 *
 * **The enforcement:** `backend/src/activateCarrierOwnershipWall.integration.test.ts`'s
 * "the carrier-ownership wall still bites a bare readied id the preparer does NOT hold" arm.
 * Red-witnessed 2026-09-06 — stubbing this function to admit everything reddens that arm plus
 * three others. If you are here because you believe the door subsumes this leg, delete it and
 * run that file: the suite will tell you no.
 */
export type IsActivateActionLegalArgs = {
  state: EncounterState;
  actorId: string;
  queriedId: string;
  overlay?: LoadedOverlay;
};

export function isActivateActionLegal(args: IsActivateActionLegalArgs): boolean {
  const { state, actorId, queriedId, overlay = SEED_OVERLAY } = args;
  const combatant = state.combatants[actorId];
  if (!combatant) return false;
  if (isActivateActionCarried(carriedGrantedActionIds(combatant, overlay), queriedId)) return true;
  if (carriedPilotFrameActionIds(combatant, overlay).has(queriedId)) return true;
  if (carriedStatusActionIds(combatant, overlay).has(queriedId)) return true;
  if (deployedActionSources(state, actorId, overlay).some((s) => s.actionId === queriedId)) return true;
  if (carriedCoreActionIds(combatant).has(queriedId)) return true;
  if (carriedBuiltinActivateIds(combatant).has(queriedId)) return true;
  return false;
}

/**
 * The full **granted-`request_activate` legality verdict** — the granted-action sibling of {@link
 * checkActionUseLegality} (core verbs), called alongside it for every `request_activate`
 * before any dice. Composes three independent gates:
 *
 *   1. **Protocol start-of-turn timing** — gated whenever the action IS Protocol-activation,
 *      REGARDLESS of whether it carries a frequency cap at all (a Protocol with no cap still
 *      needs its window enforced).
 *   2. **Protocol once per turn** (RAW Core p.62) — every Protocol, capped or not, against the
 *      protocol-safe {@link canUseProtocolAction} ledger (`actionIdForIntent` deliberately
 *      excludes a Protocol from `actionUses`), at an allowance of 1 unless a `turn`-grain cap
 *      raises it.
 *   3. **The frozen frequency cap** (the resolved {@link Affordance}'s `cap`), when present:
 *      - `grain: "round" | "scene"` routes to the shared {@link checkActionAvailable}
 *        predicate (the "one door" rule) — the SAME read a core verb's round/scene use would
 *        take.
 *      - `grain: "turn"`, NON-Protocol — already gated by {@link checkActionUseLegality}
 *        upstream (`actionIdForIntent`'s reuse of `actionUses`); this predicate stands down to
 *        avoid double-gating the same ledger through a second error message.
 *
 * An action with no frozen cap at all is unrestricted past the Protocol legs (the common case —
 * most granted actions, and every shipped Protocol in real data, carry no `frequency`).
 * Returns a verdict the room surfaces (a rejection is a logged no-op before any dice).
 */
export type CheckGrantedActionLegalityArgs = {
  state: EncounterState;
  actor: string;
  /** The overlay entity id `request_activate.action` names. */
  action: string;
  overlay?: LoadedOverlay;
};

export function checkGrantedActionLegality(args: CheckGrantedActionLegalityArgs): BudgetVerdict {
  const { state, actor, action, overlay = SEED_OVERLAY } = args;
  const atk = state.combatants[actor];
  const name = atk?.name ?? actor;
  // **One parser at the door** (`decisions/0176` ruling 3), ahead of every frozen read below.
  // `action` arrives in whichever shape the caller emitted — bare (`pg_patch`) or composite
  // (`pg_patch:0`) — and **which shape is correct is carrier-class-determined**, so it cannot be
  // indexed into any frozen map raw. {@link resolveAffordance} answers with the carrier's ONE
  // frozen record; its `cap` and `recharge` are read off that object below, so there is no
  // second lookup to key wrong. See its doc for the class split and for why `${action}:0` is the
  // wrong way to get there.
  const resolved = resolveAffordance({ state, actorId: actor, wireId: action, overlay });
  if (resolved.kind === "ambiguous") {
    // A bare granted id naming an entry that mints MORE THAN ONE composite for this carrier. The
    // granted class mints one composite per affordance precisely so each is separately
    // addressable, capped and discharged, so there is no defensible pick — name them and reject
    // before any dice. (The pilot/frame-effect class never reaches here: it mints no composites,
    // so `everest_hyperspec_fuel_injector` keeps firing both of its clauses on one bare
    // activation, correctly.) Nothing in the shipped corpus hits this today.
    const first = resolved.matches[0]!;
    const last = resolved.matches[resolved.matches.length - 1]!;
    return err(`${action}: names ${resolved.matches.length} actions — activate ${first} … ${last}`);
  }
  // The ONE frozen record. `key` is its ledger slot — identical to `action` for a composite fire
  // and for every non-granted class, so nothing that works today changes shape.
  const affordance = resolved.affordance;
  const key = affordance.key;
  // Recharge gate (`npc-features-fielded-roster` Phase 3.1, RAW Core p.281): the granted-action
  // sibling of {@link import("./weaponState").checkWeaponRecharge} — a `tg_recharge`-tagged
  // granted action that has activated stays **discharged** until a start-of-turn 1d6 recharge
  // roll (`resolveRecharge`) clears it, checked here, before any dice, ahead of every other
  // arm in this function (the Protocol-timing/frequency-cap legality below is orthogonal — an
  // action can be BOTH Protocol-gated and Recharge-gated in principle, and a discharged item
  // should reject on that fact regardless). Verdict wording deliberately mirrors
  // `checkWeaponRecharge`'s (`"discharged (Recharge N+)"`), so the CLI/browser row reads the same
  // way whether the discharged item is a weapon or a granted action.
  //
  // **The target comes off the record, and the ledger off `key`** — the SAME key
  // `resolveActivate`'s `discharge_set` step writes (`resolve/activate.ts`), so a bare fire and a
  // composite fire of one granted entry hit one discharge slot. The ledger is
  // `current.affordanceState`, the one per-affordance table (`decisions/0176` ruling 1), which
  // carries weapon instances and granted actions alike — the same read
  // {@link import("./weaponState").checkWeaponRecharge} takes.
  const rechargeTarget = affordance.recharge;
  if (rechargeTarget !== undefined && !affordable({ kind: "weapon", carrier: actor, key, spend: "discharge" }, state, undefined)) {
    return err(
      `${action}: ${name} is discharged (Recharge ${rechargeTarget}+) — awaits ${name}'s next start-of-turn recharge roll`,
    );
  }
  const protocol = isProtocolAction(actor, action, overlay);
  // RAW Core p.62: a Protocol is taken at the start of YOUR turn. `isTurnVirgin` reads the idle
  // tier as open (the economy is inert with no turn running), so once combat has begun — any unit
  // has activated — the gap between turns is refused here. Before the first activation nothing is
  // refused, so an out-of-combat Protocol still works (a foreman ruling on RAW grounds, from
  // game-night's diagnosis of an off-turn Power Up).
  if (protocol && state.activation.turn.phase === "idle" && state.activation.turnOrdinal > 0) {
    return err(`${action}: ${name} can only activate this Protocol at the start of its own turn — no turn is open`);
  }
  if (protocol && !isTurnVirgin(state, actor)) {
    return err(
      `${action}: ${name} can only activate this Protocol at the start of its turn, before moving or taking any other action`,
    );
  }
  const cap = affordance.cap;
  // RAW Core p.62: "Each Protocol can only be taken once per turn." A property of the class, not of
  // an authored cap — no shipped Protocol freezes one — so this wall runs for every Protocol, ahead
  // of the cap arms, at the RAW allowance of 1 unless a `turn`-grain cap says otherwise. It reads
  // the protocol-safe `protocolUses` ledger on the resolved `key`, the slot `refereeIntent.ts`'s
  // Protocol fold writes for every Protocol activation.
  if (protocol) {
    const turn = state.activation.turn;
    const allowance = cap?.grain === "turn" ? cap.max : 1;
    if (turn.phase === "acting" && turn.actor === actor && !affordable({ kind: "action_points", carrier: actor, points: 0, protocolActionId: key, allowance }, state, undefined)) {
      return err(`${action}: ${name} has already used this Protocol this turn (once per turn)`);
    }
  }
  // Every fact present gates (`decisions/0202`): the rate on its grain's ledger at `key`, then the
  // Limited pool on the per-affordance ledger at `pool ?? key`. Drop Bomb carries both, so its second
  // bomb in a round is refused on the rate and its fourth on the pool.
  //
  // `key`, not `action`: the frozen facts this ledger counts against are per-affordance, so their
  // consumed-count slots have to be too, or a bare fire and a composite fire of one granted entry
  // would each get their own full allowance. **The write half is routed through the SAME door**
  // — `refereeIntent.ts`'s `spend_action_use` fold resolves the affordance and folds one spend per
  // fact on these same slots (`projects/affordance-table.md` Phase 1.4) — so this read and that
  // write name one slot. Landing the read without the write is invisible to every
  // reproduction: a correct read against a slot nothing writes gates nothing, silently.
  if (cap?.grain === "round" || cap?.grain === "scene") {
    const rate = checkActionAvailable({ state, actor, actionId: key, grain: cap.grain, max: cap.max });
    if (!rate.ok) return rate;
  }
  const limited = affordance.limited;
  if (limited) return checkActionAvailable({ state, actor, actionId: limited.pool ?? key, grain: "scene", max: limited.max });
  // A `turn` rate: a non-Protocol granted action already rides `actionUses` via
  // `actionIdForIntent`/`checkActionUseLegality` upstream, keyed on this same resolved `key`
  // (that derivation goes through this file's door too), so both id shapes share its slot; a
  // Protocol's turn cap was read by the Protocol wall above.
  return ok(undefined);
}

